import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {FONT_FAMILY_PROXIMA, LEGACY_FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import {GRAY, GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface GroupCollapserProps {
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}

interface GroupTitleBarProps extends GroupCollapserProps {
  icon?: BenchlingIconName | null;
  rightHeaderControls?: JSX.Element | null;
  onTitleContextMenu?: React.MouseEventHandler<HTMLDivElement>;

  // The header of the group may be combined of `groupTypeLabel` and `groupName`. If
  // both are present, `groupTypeLabel` would represent the type of the group, such as `Mixture Prep`
  // and `groupName` would be the unique name of the group.
  groupTypeLabel?: string;
  groupName: string | JSX.Element;
  viewer?: boolean;
}

function GroupCollapser(props: GroupCollapserProps): JSX.Element {
  const text = props.isCollapsed ? 'Expand group' : 'Collapse group';
  return (
    <div
      className={classNames(
        css(styles.collapserChevronWrapper),
        'notebook-export--nlg-collapser-chevron-wrapper'
      )}
      {...getTestProps({component: 'GroupCollapser', element: 'root'})}
    >
      <TooltipWrapper tooltipTitle={text}>
        <BenchlingIcon
          className={classNames(css(styles.collapserIcon), 'notebook-export--nlg-collapser-icon')}
          icon={props.isCollapsed ? 'chevron-down' : 'chevron-up'}
          altText={text}
          onClick={props.onToggleCollapsed}
        />
      </TooltipWrapper>
    </div>
  );
}

export default function GroupTitleBar(props: GroupTitleBarProps): JSX.Element {
  // Print view (i.e. viewer === true) should render the title bar as expanded.
  const isCollapsed = props.viewer ? false : props.isCollapsed;

  return (
    <div
      className={classNames(css(styles.titleBar), 'notebook-export--nlg-title-bar')}
      {...getTestProps({component: 'GroupTitleBar', element: 'root'})}
    >
      <div className="u-flex-grow" onContextMenu={props.onTitleContextMenu}>
        <div className="u-flex-display">
          {props.icon && <BenchlingIcon className="mediocre-tableEditable-tableIcon" icon={props.icon} />}
          {props.groupTypeLabel}
          {props.groupName && (
            <div
              className={classNames(
                classNames(css(styles.groupName), 'notebook-export--nlg-group-name'),
                // Only style this in grey if there is a group label
                props.groupTypeLabel != null &&
                  classNames(css(styles.groupNameSubtitle), 'notebook-export--nlg-group-name-subtitle')
              )}
              {...getTestProps({component: 'GroupTitleBar', element: 'groupName'})}
            >
              {props.groupName}
            </div>
          )}
        </div>
      </div>
      {!props.viewer && <div className="u-flex-none">{props.rightHeaderControls}</div>}
      <GroupCollapser isCollapsed={isCollapsed} onToggleCollapsed={props.onToggleCollapsed} />
    </div>
  );
}

const styles = StyleSheet.create({
  titleBar: {
    alignItems: 'center',
    borderBottom: `1px solid ${GRAY}`,
    color: NEAR_BLACK,
    display: 'flex',
    fontFamily: FONT_FAMILY_PROXIMA,
    ...LEGACY_FONT_PROXIMA_TITLE,
    height: '36px',
    lineHeight: 1,
    padding: '5px 10px',
  },

  groupName: {
    color: NEAR_BLACK,
    overflow: 'hidden',
    padding: '0 10px 0 0',
    textOverflow: 'ellipsis',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },

  groupNameSubtitle: {
    color: GRAY_DARKER,
    display: 'flex',
    padding: '0 8px',
  },

  collapserChevronWrapper: {
    position: 'absolute',
    right: '-27px',
  },

  collapserIcon: {
    cursor: 'pointer',
    fill: GRAY,
    height: '16px',
    width: '16px',
  },
});
